// HeroSlideshow.js
//
// Full-bleed crossfading background for the hero:
//  - Ken Burns drift on each slide, four variants so a repeat never looks identical
//  - Auto-advance, with swipe left/right on touch and clickable dots
//  - Pauses on hover, on focus, when the tab is hidden, and for reduced motion
//
// Ported from the zomigermany hero (Next/Tailwind) to this CRA + plain CSS app.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPause, FaPlay, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './HeroSlideshow.css';

// Four drift directions, applied round-robin, so consecutive slides never pan
// the same way. Defined in HeroSlideshow.css.
const KEN_CLASSES = ['ken-1', 'ken-2', 'ken-3', 'ken-4'];

// A swipe shorter than this is a tap or a scroll, not a deliberate gesture.
const SWIPE_THRESHOLD_PX = 50;

const HeroSlideshow = ({ slides, intervalMs = 6000, onActiveChange }) => {
  const [active, setActive] = useState(0);
  const [stopped, setStopped] = useState(false);
  const pausedRef = useRef(false);
  const touchStartRef = useRef(null);
  const swipedRef = useRef(false);
  const { t } = useTranslation();

  // Which slides have been added to the DOM.
  //
  // Every slide sits at inset 0, so they are all technically in the viewport
  // and merely transparent: loading="lazy" would defer nothing, because lazy
  // loading triggers on intersection, not on visibility. Mounting is the only
  // lever that actually withholds the bytes — start with the first slide and
  // the one after it, then admit each next slide as it is approached.
  const [mounted, setMounted] = useState(
    () => new Set(slides.length > 1 ? [0, 1] : [0])
  );

  useEffect(() => {
    setMounted(prev => {
      const next = (active + 1) % slides.length;
      if (prev.has(next)) return prev;
      const updated = new Set(prev);
      updated.add(next);
      return updated;
    });
  }, [active, slides.length]);

  const goTo = useCallback(
    index => setActive((index + slides.length) % slides.length),
    [slides.length]
  );

  // Report the active slide upward so the hero headline can follow it. Done in
  // an effect, not inside the setState call — notifying a parent during render
  // would set its state mid-render.
  useEffect(() => {
    if (onActiveChange) onActiveChange(active);
  }, [active, onActiveChange]);

  useEffect(() => {
    if (slides.length < 2 || stopped) return;

    // Someone who asked the OS for less motion should not get an animation
    // that restarts every few seconds.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => {
      // Skipping rather than clearing keeps the rhythm: the slideshow resumes
      // on the next tick instead of waiting a fresh full interval.
      if (pausedRef.current || document.hidden) return;
      setActive(i => (i + 1) % slides.length);
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [slides.length, intervalMs, stopped]);

  const handleTouchStart = e => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = e => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    // Only act on a mostly-horizontal gesture, or a vertical page scroll that
    // drifts sideways would flip the slide out from under the reader.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

    // A swipe is followed by a click event on touch devices. Without this the
    // gesture would both change the slide and navigate away from the hero.
    swipedRef.current = true;

    goTo(dx < 0 ? active + 1 : active - 1);
  };

  const handleSlideClick = () => {
    if (swipedRef.current) {
      swipedRef.current = false;
      return;
    }

    const targetId = slides[active]?.targetId;
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    // `block` scrolls the page down to the section; `inline` scrolls the
    // snapped row sideways to this card on phones, so the click lands directly
    // on the right video in either layout rather than on whichever card the
    // row happened to be showing. `scroll-margin-top` on the target keeps the
    // fixed header off the heading.
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'start',
      inline: 'center'
    });
  };

  const handleKeyDown = e => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    }
  };

  return (
    <div
      className="hero-slideshow"
      role="group"
      aria-roledescription="carousel"
      aria-label={t('hero.slideshowLabel', 'Wedding and baby reveal photos')}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.src}
          className={`hero-slide ${i === active ? 'is-active' : ''}`}
          aria-hidden={i !== active}
        >
          <div className={`hero-slide-inner ${KEN_CLASSES[i % KEN_CLASSES.length]}`}>
            {mounted.has(i) ? (
              <img
                src={slide.src}
                alt={slide.alt}
                className="hero-slide-img"
                style={slide.objectPosition ? { objectPosition: slide.objectPosition } : undefined}
              />
            ) : null}
          </div>
        </div>
      ))}

      {/* Darkening layers, so the hero text stays legible over any photo. */}
      <div className="hero-slide-scrim" />

      {/* Clicking the photo goes to that event's video. Rendered as a real
          button covering the hero rather than a click handler on the slide
          div, so it is focusable and announced; it sits above the hero text,
          which is not interactive, and below the dots, which are not. */}
      {slides[active]?.targetId ? (
        <button
          type="button"
          className="hero-slide-link"
          onClick={handleSlideClick}
          aria-label={slides[active].linkLabel}
        >
          <span className="hero-slide-link-label">
            <FaPlay aria-hidden /> {slides[active].linkLabel}
          </span>
        </button>
      ) : null}

      {/* Previous / next. A mouse user otherwise has no way to step through
          the slides: swiping is touch-only, and clicking the photo navigates
          to the video rather than advancing. These sit above the click layer
          so pressing one changes the slide instead of leaving the hero. */}
      {slides.length > 1 ? (
        <>
          <button
            type="button"
            className="hero-arrow hero-arrow-prev"
            onClick={() => goTo(active - 1)}
            aria-label={t('hero.prevSlide', 'Forrige bilde')}
          >
            <FaChevronLeft aria-hidden />
          </button>
          <button
            type="button"
            className="hero-arrow hero-arrow-next"
            onClick={() => goTo(active + 1)}
            aria-label={t('hero.nextSlide', 'Neste bilde')}
          >
            <FaChevronRight aria-hidden />
          </button>
        </>
      ) : null}

      {slides.length > 1 ? (
        <div className="hero-slideshow-controls">
          {slides.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              className={`hero-dot ${i === active ? 'is-active' : ''}`}
              aria-label={t('hero.goToSlide', {
                n: i + 1,
                total: slides.length,
                defaultValue: 'Go to photo {{n}} of {{total}}'
              })}
              aria-current={i === active}
              onClick={() => goTo(i)}
            />
          ))}

          {/* WCAG 2.2.2 (Pause, Stop, Hide): anything that moves on its own for
              more than five seconds needs a real way to stop it. Hovering does
              not count — keyboard and touch users cannot hover — and the dots
              jump without stopping, so this has to be its own control. */}
          <button
            type="button"
            className="hero-playpause"
            onClick={() => setStopped(v => !v)}
            aria-pressed={stopped}
            aria-label={
              stopped
                ? t('hero.playSlideshow', 'Play slideshow')
                : t('hero.pauseSlideshow', 'Pause slideshow')
            }
          >
            {stopped ? <FaPlay aria-hidden /> : <FaPause aria-hidden />}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default HeroSlideshow;
